/**
 * Online/offline status for the offline-mode banner.
 *
 * Drives React Query's `onlineManager` so queries pause/resume correctly, and
 * exposes the current status to the UI. On native we use @capacitor/network
 * because `navigator.onLine` is unreliable inside the Android WebView; on web we
 * let onlineManager use its default navigator.onLine + window event handling.
 */
import { useSyncExternalStore, useEffect } from 'react'
import { onlineManager } from '@tanstack/react-query'
import { Network } from '@capacitor/network'
import { isNative } from '@/lib/capacitor'

// Bridge Capacitor Network -> onlineManager on native. Idempotent: onlineManager
// keeps a single source of truth, so calling setOnline repeatedly is safe.
let nativeBridgeStarted = false
function startNativeNetworkBridge() {
  if (nativeBridgeStarted || !isNative()) return
  nativeBridgeStarted = true

  Network.getStatus()
    .then((status) => onlineManager.setOnline(status.connected))
    .catch(() => { /* leave onlineManager at its default */ })

  Network.addListener('networkStatusChange', (status) => {
    onlineManager.setOnline(status.connected)
  })
}

export function useOnlineStatus(): boolean {
  useEffect(() => {
    startNativeNetworkBridge()
  }, [])

  return useSyncExternalStore(
    (cb) => onlineManager.subscribe(cb),
    () => onlineManager.isOnline(),
    () => true, // SSR/initial: assume online
  )
}
